// frontend/src/components/Dashboard/ProductForm.jsx - COMPLETELY CLEANED
import React, { useState, useEffect } from "react";
import { createProduct, updateProduct } from "../../api";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTimes,
  faEdit,
  faTrash,
  faImage,
  faTag,
  faBox,
  faUpload,
  faDollarSign,
  faWeightScale,
  faStar,
  faClipboard,
  faCheck,
  faSpinner,
  faExclamationTriangle,
  faInfoCircle,
  faSave,
  faGlobe,
  faShip,
  faRuler,
  faBoxes,
  faCheckCircle,
  faBuilding,
  faAnchor,
  faLocationDot,
  faCalendarCheck,
  faClock,
  faLeaf,
  faCow,
  faTractor,
  faUsers,
  faMobileAlt,
  faMoneyBillWave,
  faCreditCard,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";

// ============================================
// CONSTANTS
// ============================================
const SUPPLIER = {
  name: "Globally Sourced",
  country: "Globally Sourced",
  city: "Globally Sourced",
  portLoading: "Globally Sourced",
  portDischarge: "Freetown, Sierra Leone",
};
const AVAILABLE_SIZES = [2, 10, 25, 50];
const PREORDER_SIZES = [25, 50];
const USD_TO_SLL_RATE = 24.65;

const CATEGORIES = [
  {
    value: "rumen-bypass-fat",
    label: "Rumen Bypass Fat (GrowPro™)",
    icon: faCow,
  },
  { value: "mineral-blocks", label: "Mineral Blocks", icon: faStar },
  {
    value: "protein-concentrates",
    label: "Protein Concentrates",
    icon: faLeaf,
  },
  { value: "feed-supplements", label: "Feed Supplements", icon: faBox },
];

const DEFAULT_PRICE_TIERS = [
  {
    userType: "customer",
    minQuantity: 1,
    maxQuantity: 24,
    discountPercent: 0,
    pricePerUnit: { usd: 4.2, sll: Math.round(4.2 * USD_TO_SLL_RATE) },
  },
  {
    userType: "customer",
    minQuantity: 25,
    maxQuantity: 49,
    discountPercent: 5,
    pricePerUnit: { usd: 3.99, sll: Math.round(3.99 * USD_TO_SLL_RATE) },
  },
  {
    userType: "customer",
    minQuantity: 50,
    maxQuantity: 99,
    discountPercent: 10,
    pricePerUnit: { usd: 3.78, sll: Math.round(3.78 * USD_TO_SLL_RATE) },
  },
  {
    userType: "farmer",
    minQuantity: 1,
    maxQuantity: 24,
    discountPercent: 0,
    pricePerUnit: { usd: 4.2, sll: Math.round(4.2 * USD_TO_SLL_RATE) },
  },
  {
    userType: "farmer",
    minQuantity: 25,
    maxQuantity: 49,
    discountPercent: 5,
    pricePerUnit: { usd: 3.99, sll: Math.round(3.99 * USD_TO_SLL_RATE) },
  },
  {
    userType: "farmer",
    minQuantity: 50,
    maxQuantity: 99,
    discountPercent: 10,
    pricePerUnit: { usd: 3.78, sll: Math.round(3.78 * USD_TO_SLL_RATE) },
  },
  {
    userType: "farmer",
    minQuantity: 100,
    maxQuantity: 499,
    discountPercent: 15,
    pricePerUnit: { usd: 3.57, sll: Math.round(3.57 * USD_TO_SLL_RATE) },
  },
  {
    userType: "farmer",
    minQuantity: 500,
    maxQuantity: 999,
    discountPercent: 20,
    pricePerUnit: { usd: 3.36, sll: Math.round(3.36 * USD_TO_SLL_RATE) },
  },
  {
    userType: "farmer",
    minQuantity: 1000,
    maxQuantity: null,
    discountPercent: 25,
    pricePerUnit: { usd: 3.15, sll: Math.round(3.15 * USD_TO_SLL_RATE) },
  },
];

const DEFAULT_BULK_OPTIONS = [
  { quantity: 25, label: "25kg (Starter)", discountPercent: 5, isActive: true },
  { quantity: 50, label: "50kg (Farmer)", discountPercent: 10, isActive: true },
  {
    quantity: 100,
    label: "100kg (Wholesale)",
    discountPercent: 15,
    isActive: true,
  },
  {
    quantity: 500,
    label: "500kg (Bulk Farmer)",
    discountPercent: 20,
    isActive: true,
  },
  {
    quantity: 1000,
    label: "1 Ton (Enterprise)",
    discountPercent: 25,
    isActive: true,
  },
];

const DEFAULT_BENEFITS = [
  "Increases milk fat percentage by up to 4.2%",
  "Boosts milk production by 1.5-3.0 kg per cow per day",
  "Improves feed efficiency and conversion rates",
  "Supports body condition and reproductive performance",
  "Reduces heat stress and improves overall herd health",
];

const DEFAULT_SPECIFICATIONS = {
  "Crude Fat": "85% min",
  "Crude Fiber": "1% max",
  Moisture: "2% max",
  "Metabolizable Energy": "32 MJ/kg",
  "Rumen Bypass": "85% protected",
  "Palmitic Acid (C16:0)": "70% min",
  "Stearic Acid (C18:0)": "25% min",
  Calcium: "0.5% min",
  Source: "Malaysia",
};

// ============================================
// MAIN COMPONENT
// ============================================
const ProductForm = ({ product, onClose, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState("basic");
  const [validationErrors, setValidationErrors] = useState({});
  const [newBenefit, setNewBenefit] = useState("");
  const [newSpecKey, setNewSpecKey] = useState("");
  const [newSpecValue, setNewSpecValue] = useState("");
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState(product?.images || []);
  const [showTiers, setShowTiers] = useState(true);
  const [showBulkOptions, setShowBulkOptions] = useState(true);

  const isGrowPro =
    product?.slug === "kenali-growpro" ||
    product?.name?.includes("GrowPro") ||
    product?.category === "rumen-bypass-fat";

  const [formData, setFormData] = useState({
    name: product?.name || (isGrowPro ? "Kenali GrowPro™" : ""),
    slug: product?.slug || (isGrowPro ? "kenali-growpro" : ""),
    category: product?.category || "rumen-bypass-fat",
    description:
      product?.description ||
      (isGrowPro
        ? "Kenali GrowPro™ is a premium rumen bypass fat supplement designed to enhance milk production and improve dairy cow performance. Formulated with high-quality bypass fats, it provides concentrated energy that bypasses the rumen for direct absorption in the small intestine, maximizing milk yield and butterfat content."
        : ""),
    shortDescription:
      product?.shortDescription ||
      (isGrowPro
        ? "Premium rumen bypass fat for enhanced milk production and dairy cow performance."
        : ""),
    basePrice: {
      usd: product?.basePrice?.usd || (isGrowPro ? 4.2 : 0),
      sll:
        product?.basePrice?.sll ||
        (isGrowPro ? Math.round(4.2 * USD_TO_SLL_RATE) : 0),
    },
    unit: product?.unit || "kg",
    unitSize: product?.unitSize || 25,
    packaging: product?.packaging || "25kg premium bag with moisture barrier",
    stock: product?.stock || (isGrowPro ? 500 : 0),
    lowStockThreshold: product?.lowStockThreshold || 100,
    isAvailable: product?.isAvailable !== false,
    isPreOrderAvailable: product?.isPreOrderAvailable !== false,
    featured: product?.featured !== undefined ? product.featured : true,
    featuredOrder: product?.featuredOrder || 1,
    origin: product?.origin || SUPPLIER.country,
    supplierName: product?.supplierName || SUPPLIER.name,
    supplierCountry: product?.supplierCountry || SUPPLIER.country,
    supplierCity: product?.supplierCity || SUPPLIER.city,
    portLoading: product?.portLoading || SUPPLIER.portLoading,
    portDischarge: product?.portDischarge || SUPPLIER.portDischarge,
    farmerMinimumOrder: product?.farmerMinimumOrder || 25,
    usageInstructions:
      product?.usageInstructions ||
      (isGrowPro
        ? "Feed 250-500g per cow per day, mixed thoroughly with the total mixed ration (TMR). Gradually introduce over 7-10 days. Ensure fresh, clean water is always available. Store in a cool, dry place away from direct sunlight."
        : ""),
    shippingWeight: product?.shippingWeight || 25,
    hazmat: product?.hazmat || false,
    availableSizes: product?.availableSizes || [...AVAILABLE_SIZES],
    preOrderSizes: product?.preOrderSizes || [...PREORDER_SIZES],
    priceTiers:
      product?.priceTiers?.length > 0
        ? product.priceTiers
        : [...DEFAULT_PRICE_TIERS],
    bulkOptions:
      product?.bulkOptions?.length > 0
        ? product.bulkOptions
        : [...DEFAULT_BULK_OPTIONS],
    benefits:
      product?.benefits?.length > 0 ? product.benefits : [...DEFAULT_BENEFITS],
    specifications:
      Object.keys(product?.specifications || {}).length > 0
        ? product.specifications
        : { ...DEFAULT_SPECIFICATIONS },
    metaTitle:
      product?.metaTitle ||
      (isGrowPro
        ? "Kenali GrowPro™ - Premium Rumen Bypass Fat for Dairy Cows"
        : ""),
    metaDescription:
      product?.metaDescription ||
      (isGrowPro
        ? "Kenali GrowPro™ is a premium rumen bypass fat supplement for dairy cows. Increases milk production and butterfat content. Available in 25kg and 50kg sizes."
        : ""),
    metaKeywords:
      product?.metaKeywords?.length > 0
        ? product.metaKeywords
        : [
            "rumen bypass fat",
            "dairy supplement",
            "cow feed",
            "milk production",
            "Kenali Enterprise",
            "GrowPro",
          ],
    averageRating: product?.averageRating || 0,
    commentCount: product?.commentCount || 0,
  });

  // Auto-generate slug from name
  useEffect(() => {
    if (!product && formData.name) {
      const slug = formData.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
      setFormData((prev) => ({ ...prev, slug }));
    }
  }, [formData.name, product]);

  // Auto-generate meta title
  useEffect(() => {
    if (!formData.metaTitle && formData.name) {
      setFormData((prev) => ({
        ...prev,
        metaTitle: `${formData.name} - Kenali Enterprise | Premium Livestock Feed`,
      }));
    }
  }, [formData.name, formData.metaTitle]);

  // Set default supplier info
  useEffect(() => {
    if (!product) {
      setFormData((prev) => ({
        ...prev,
        origin: prev.origin || SUPPLIER.country,
        supplierName: prev.supplierName || SUPPLIER.name,
        supplierCountry: prev.supplierCountry || SUPPLIER.country,
        supplierCity: prev.supplierCity || SUPPLIER.city,
        portLoading: prev.portLoading || SUPPLIER.portLoading,
        portDischarge: prev.portDischarge || SUPPLIER.portDischarge,
      }));
    }
  }, [product]);

  // ============================================
  // HANDLERS
  // ============================================
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox") {
      setFormData({ ...formData, [name]: checked });
    } else if (name === "priceUSD") {
      const usdPrice = parseFloat(value) || 0;
      setFormData({
        ...formData,
        basePrice: {
          ...formData.basePrice,
          usd: usdPrice,
          sll: Math.round(usdPrice * USD_TO_SLL_RATE),
        },
      });
    } else if (name === "availableSizes") {
      const sizes = value
        .split(",")
        .map((s) => parseInt(s.trim()))
        .filter((s) => !isNaN(s) && s > 0);
      setFormData({
        ...formData,
        availableSizes: sizes.length ? sizes : [...AVAILABLE_SIZES],
      });
    } else if (name === "preOrderSizes") {
      const sizes = value
        .split(",")
        .map((s) => parseInt(s.trim()))
        .filter((s) => !isNaN(s) && s > 0);
      setFormData({
        ...formData,
        preOrderSizes: sizes.length ? sizes : [...PREORDER_SIZES],
      });
    } else if (name.startsWith("bulkOptions.")) {
      const [_, index, field] = name.split(".");
      handleBulkOptionChange(parseInt(index), field, value);
    } else if (name.startsWith("priceTiers.")) {
      const [_, index, field] = name.split(".");
      handleTierChange(parseInt(index), field, value);
    } else {
      setFormData({ ...formData, [name]: value });
    }
    setError("");
    setValidationErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleTierChange = (index, field, value) => {
    const updatedTiers = [...formData.priceTiers];
    if (field === "maxQuantity" && value === "") {
      updatedTiers[index][field] = null;
    } else if (field === "userType") {
      updatedTiers[index][field] = value;
    } else {
      updatedTiers[index][field] =
        field === "discountPercent"
          ? parseFloat(value) || 0
          : parseInt(value) || 0;

      if (field === "discountPercent" || field === "minQuantity") {
        const baseUsd = formData.basePrice.usd || 4.2;
        const discount = updatedTiers[index].discountPercent || 0;
        const tierPriceUsd = baseUsd * (1 - discount / 100);
        updatedTiers[index].pricePerUnit = {
          usd: parseFloat(tierPriceUsd.toFixed(2)),
          sll: Math.round(tierPriceUsd * USD_TO_SLL_RATE),
        };
      }
    }
    setFormData({ ...formData, priceTiers: updatedTiers });
  };

  const addTier = () => {
    setFormData({
      ...formData,
      priceTiers: [
        ...formData.priceTiers,
        {
          userType: "farmer",
          minQuantity: 0,
          maxQuantity: null,
          discountPercent: 0,
          pricePerUnit: {
            usd: formData.basePrice.usd || 4.2,
            sll: Math.round((formData.basePrice.usd || 4.2) * USD_TO_SLL_RATE),
          },
        },
      ],
    });
  };

  const removeTier = (index) => {
    if (formData.priceTiers.length <= 1) {
      setError("Must have at least one price tier");
      return;
    }
    const updatedTiers = formData.priceTiers.filter((_, i) => i !== index);
    setFormData({ ...formData, priceTiers: updatedTiers });
  };

  const handleBulkOptionChange = (index, field, value) => {
    const updatedOptions = [...formData.bulkOptions];
    if (field === "isActive") {
      updatedOptions[index][field] = value;
    } else if (field === "label") {
      updatedOptions[index][field] = value;
    } else {
      updatedOptions[index][field] =
        field === "discountPercent"
          ? parseFloat(value) || 0
          : parseInt(value) || 0;
    }
    setFormData({ ...formData, bulkOptions: updatedOptions });
  };

  const addBulkOption = () => {
    setFormData({
      ...formData,
      bulkOptions: [
        ...formData.bulkOptions,
        { quantity: 0, label: "", discountPercent: 0, isActive: true },
      ],
    });
  };

  const removeBulkOption = (index) => {
    if (formData.bulkOptions.length <= 1) {
      setError("Must have at least one bulk option");
      return;
    }
    const updatedOptions = formData.bulkOptions.filter((_, i) => i !== index);
    setFormData({ ...formData, bulkOptions: updatedOptions });
  };

  const handleAddBenefit = () => {
    if (newBenefit.trim()) {
      setFormData({
        ...formData,
        benefits: [...formData.benefits, newBenefit.trim()],
      });
      setNewBenefit("");
    }
  };

  const handleRemoveBenefit = (index) => {
    const updatedBenefits = formData.benefits.filter((_, i) => i !== index);
    setFormData({ ...formData, benefits: updatedBenefits });
  };

  const handleAddSpecification = () => {
    if (newSpecKey.trim() && newSpecValue.trim()) {
      setFormData({
        ...formData,
        specifications: {
          ...formData.specifications,
          [newSpecKey.trim()]: newSpecValue.trim(),
        },
      });
      setNewSpecKey("");
      setNewSpecValue("");
    }
  };

  const handleRemoveSpecification = (key) => {
    const updatedSpecs = { ...formData.specifications };
    delete updatedSpecs[key];
    setFormData({ ...formData, specifications: updatedSpecs });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => file.size <= 5 * 1024 * 1024);
    if (validFiles.length !== files.length) {
      setError("Some files exceed the 5MB limit");
    }
    setImages([...images, ...validFiles]);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const removeExistingImage = (index) => {
    setExistingImages(existingImages.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Product name is required";
    if (!formData.slug.trim()) errors.slug = "Slug is required";
    if (!formData.description.trim())
      errors.description = "Description is required";
    if (!formData.basePrice.usd || formData.basePrice.usd <= 0) {
      errors.priceUSD = "Base price must be greater than 0";
    }
    if (formData.stock < 0) errors.stock = "Stock cannot be negative";
    if (formData.lowStockThreshold < 0) {
      errors.lowStockThreshold = "Low stock threshold cannot be negative";
    }
    if (formData.farmerMinimumOrder < 0) {
      errors.farmerMinimumOrder = "Farmer minimum order cannot be negative";
    }
    if (!formData.origin.trim()) errors.origin = "Origin is required";
    if (!formData.availableSizes.length) {
      errors.availableSizes = "At least one available size is required";
    }

    formData.priceTiers.forEach((tier, index) => {
      if (tier.minQuantity < 0) {
        errors[`priceTiers.${index}.minQuantity`] =
          "Min quantity cannot be negative";
      }
      if (tier.discountPercent < 0 || tier.discountPercent > 50) {
        errors[`priceTiers.${index}.discountPercent`] =
          "Discount must be between 0-50%";
      }
    });

    formData.bulkOptions.forEach((option, index) => {
      if (option.quantity < 0) {
        errors[`bulkOptions.${index}.quantity`] = "Quantity cannot be negative";
      }
      if (option.discountPercent < 0 || option.discountPercent > 50) {
        errors[`bulkOptions.${index}.discountPercent`] =
          "Discount must be between 0-50%";
      }
      if (!option.label.trim()) {
        errors[`bulkOptions.${index}.label`] = "Label is required";
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError("Please fix all validation errors before submitting");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const data = new FormData();

      const fieldsToSend = {
        name: formData.name.trim(),
        slug: formData.slug.trim().toLowerCase(),
        category: formData.category,
        description: formData.description.trim(),
        shortDescription: formData.shortDescription || "",
        unit: formData.unit,
        unitSize: formData.unitSize,
        packaging: formData.packaging,
        stock: formData.stock,
        lowStockThreshold: formData.lowStockThreshold,
        isAvailable: formData.isAvailable,
        isPreOrderAvailable: formData.isPreOrderAvailable,
        featured: formData.featured,
        featuredOrder: formData.featuredOrder,
        origin: formData.origin || SUPPLIER.country,
        supplierName: formData.supplierName || SUPPLIER.name,
        supplierCountry: formData.supplierCountry || SUPPLIER.country,
        supplierCity: formData.supplierCity || SUPPLIER.city,
        portLoading: formData.portLoading || SUPPLIER.portLoading,
        portDischarge: formData.portDischarge || SUPPLIER.portDischarge,
        farmerMinimumOrder: formData.farmerMinimumOrder,
        usageInstructions: formData.usageInstructions || "",
        shippingWeight: formData.shippingWeight,
        hazmat: formData.hazmat,
        metaTitle: formData.metaTitle || `${formData.name} - Kenali Enterprise`,
        metaDescription:
          formData.metaDescription || formData.description.substring(0, 160),
      };

      const complexFields = {
        basePrice: formData.basePrice,
        availableSizes: formData.availableSizes,
        preOrderSizes: formData.preOrderSizes,
        priceTiers: formData.priceTiers,
        bulkOptions: formData.bulkOptions,
        benefits: formData.benefits,
        specifications: formData.specifications,
        metaKeywords: formData.metaKeywords,
      };

      Object.entries(fieldsToSend).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          data.append(key, String(value));
        }
      });

      Object.entries(complexFields).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          data.append(key, JSON.stringify(value));
        }
      });

      if (images.length > 0) {
        images.forEach((img) => {
          data.append("images", img);
        });
      }

      if (existingImages.length > 0) {
        data.append("existingImages", JSON.stringify(existingImages));
      }

      let response;
      if (product) {
        response = await updateProduct(product._id, data);
      } else {
        response = await createProduct(data);
      }

      if (response.data.success) {
        setSuccess("Product saved successfully!");
        setTimeout(() => {
          if (onSave) onSave(response.data.data);
          if (onClose) onClose();
        }, 1500);
      }
    } catch (error) {
      console.error("Error saving product:", error);
      setError(
        error.response?.data?.message ||
          error.message ||
          "Error saving product. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // RENDER FUNCTIONS
  // ============================================
  const renderTabs = () => {
    const tabs = [
      { id: "basic", label: "Basic", icon: faTag },
      { id: "pricing", label: "Pricing", icon: faDollarSign },
      { id: "descriptions", label: "Descriptions", icon: faClipboard },
      { id: "specs", label: "Specs", icon: faInfoCircle },
      { id: "inventory", label: "Inventory", icon: faBox },
      { id: "images", label: "Images", icon: faImage },
      { id: "supplier", label: "Supplier", icon: faShip },
      { id: "seo", label: "SEO", icon: faGlobe },
      { id: "status", label: "Status", icon: faCheck },
    ];

    return (
      <div className="product-form-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={activeTab === tab.id ? "active" : ""}
            onClick={() => setActiveTab(tab.id)}
          >
            <FontAwesomeIcon icon={tab.icon} /> {tab.label}
          </button>
        ))}
      </div>
    );
  };

  const renderBasicTab = () => (
    <div className="product-form-section">
      <div className="growpro-banner">
        <FontAwesomeIcon icon={faCow} className="growpro-icon" />
        <div>
          <strong>Kenali GrowPro™ Focus</strong>
          <span>
            This product is optimized for the Kenali GrowPro™ rumen bypass fat
            supplement.
          </span>
        </div>
        <span className="growpro-badge">
          <FontAwesomeIcon icon={faCheckCircle} /> Primary Product
        </span>
      </div>

      <h3>
        <FontAwesomeIcon icon={faTag} /> Basic Information
      </h3>

      <div className="form-row">
        <div className="form-group">
          <label>Product Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={validationErrors.name ? "error" : ""}
            placeholder="e.g., Kenali GrowPro™"
          />
          {validationErrors.name && (
            <span className="error-text">{validationErrors.name}</span>
          )}
        </div>
        <div className="form-group">
          <label>Slug *</label>
          <input
            type="text"
            name="slug"
            value={formData.slug}
            onChange={handleChange}
            className={validationErrors.slug ? "error" : ""}
            placeholder="e.g., kenali-growpro"
          />
          {validationErrors.slug && (
            <span className="error-text">{validationErrors.slug}</span>
          )}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Category *</label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
          >
            {CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faGlobe} /> Origin *
          </label>
          <input
            type="text"
            name="origin"
            value={formData.origin}
            onChange={handleChange}
            placeholder="e.g., Malaysia"
            className={validationErrors.origin ? "error" : ""}
          />
          {validationErrors.origin && (
            <span className="error-text">{validationErrors.origin}</span>
          )}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faWeightScale} /> Unit
          </label>
          <select name="unit" value={formData.unit} onChange={handleChange}>
            <option value="kg">Kilogram (kg)</option>
            <option value="ton">Ton</option>
            <option value="block">Block</option>
            <option value="bag">Bag</option>
          </select>
        </div>
        <div className="form-group">
          <label>Unit Size (kg per unit)</label>
          <input
            type="number"
            name="unitSize"
            value={formData.unitSize}
            onChange={handleChange}
            min="1"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Packaging</label>
          <input
            type="text"
            name="packaging"
            value={formData.packaging}
            onChange={handleChange}
            placeholder="e.g., 25kg premium bag"
          />
        </div>
        <div className="form-group">
          <label>Short Description</label>
          <input
            type="text"
            name="shortDescription"
            value={formData.shortDescription}
            onChange={handleChange}
            placeholder="Brief summary for product listings"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faRuler} /> Available Sizes (kg)
          </label>
          <input
            type="text"
            name="availableSizes"
            value={formData.availableSizes.join(", ")}
            onChange={handleChange}
            placeholder="2, 10, 25, 50"
            className={validationErrors.availableSizes ? "error" : ""}
          />
          {validationErrors.availableSizes && (
            <span className="error-text">
              {validationErrors.availableSizes}
            </span>
          )}
          <div className="size-presets">
            {AVAILABLE_SIZES.map((size) => (
              <button
                key={size}
                type="button"
                className={`size-preset-btn ${formData.availableSizes.includes(size) ? "active" : ""}`}
                onClick={() => {
                  const current = new Set(formData.availableSizes);
                  if (current.has(size)) current.delete(size);
                  else current.add(size);
                  setFormData({
                    ...formData,
                    availableSizes: Array.from(current).sort((a, b) => a - b),
                  });
                }}
              >
                {size}kg
              </button>
            ))}
          </div>
        </div>
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faBoxes} /> Pre-Order Sizes (kg)
          </label>
          <input
            type="text"
            name="preOrderSizes"
            value={formData.preOrderSizes.join(", ")}
            onChange={handleChange}
            placeholder="25, 50"
          />
          <div className="size-presets">
            {PREORDER_SIZES.map((size) => (
              <button
                key={size}
                type="button"
                className={`size-preset-btn preorder ${formData.preOrderSizes.includes(size) ? "active" : ""}`}
                onClick={() => {
                  const current = new Set(formData.preOrderSizes);
                  if (current.has(size)) current.delete(size);
                  else current.add(size);
                  setFormData({
                    ...formData,
                    preOrderSizes: Array.from(current).sort((a, b) => a - b),
                  });
                }}
              >
                {size}kg
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderPricingTab = () => (
    <div className="product-form-section">
      <h3>
        <FontAwesomeIcon icon={faDollarSign} /> Pricing
      </h3>

      <div className="pricing-info">
        <div className="exchange-rate-info">
          <FontAwesomeIcon icon={faMoneyBillWave} />
          <span>Exchange Rate: 1 USD = {USD_TO_SLL_RATE} SLL</span>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Base Price (USD per {formData.unit}) *</label>
          <input
            type="number"
            name="priceUSD"
            value={formData.basePrice.usd}
            onChange={handleChange}
            step="0.01"
            min="0.01"
            className={validationErrors.priceUSD ? "error" : ""}
          />
          {validationErrors.priceUSD && (
            <span className="error-text">{validationErrors.priceUSD}</span>
          )}
          <small className="helper-text">
            <FontAwesomeIcon icon={faMoneyBillWave} /> SLL:{" "}
            {formData.basePrice.sll?.toLocaleString() || 0}
          </small>
        </div>
        <div className="form-group">
          <label>Farmer Minimum Order (kg)</label>
          <input
            type="number"
            name="farmerMinimumOrder"
            value={formData.farmerMinimumOrder}
            onChange={handleChange}
            min="0"
            className={validationErrors.farmerMinimumOrder ? "error" : ""}
          />
          {validationErrors.farmerMinimumOrder && (
            <span className="error-text">
              {validationErrors.farmerMinimumOrder}
            </span>
          )}
        </div>
      </div>

      <div className="tiers-toggle">
        <button
          type="button"
          onClick={() => setShowTiers(!showTiers)}
          className="toggle-btn"
        >
          <FontAwesomeIcon icon={showTiers ? faEye : faEyeSlash} />
          {showTiers ? " Hide" : " Show"} Price Tiers
        </button>
      </div>
      {showTiers && (
        <div className="price-tiers-section">
          <h4>
            <FontAwesomeIcon icon={faUsers} /> Discount Tiers by User Type
          </h4>
          <div className="tiers-header">
            <span>User Type</span>
            <span>Min Qty</span>
            <span>Max Qty</span>
            <span>Discount %</span>
            <span>Price/Unit</span>
            <span></span>
          </div>
          {formData.priceTiers.map((tier, index) => (
            <div key={index} className="tier-row">
              <select
                value={tier.userType}
                onChange={(e) =>
                  handleTierChange(index, "userType", e.target.value)
                }
              >
                <option value="customer">Customer</option>
                <option value="farmer">Farmer</option>
              </select>
              <input
                type="number"
                placeholder="Min"
                value={tier.minQuantity}
                onChange={(e) =>
                  handleTierChange(index, "minQuantity", e.target.value)
                }
                min="0"
                className={
                  validationErrors[`priceTiers.${index}.minQuantity`]
                    ? "error"
                    : ""
                }
              />
              <input
                type="number"
                placeholder="Max (∞)"
                value={tier.maxQuantity || ""}
                onChange={(e) =>
                  handleTierChange(index, "maxQuantity", e.target.value)
                }
                min="0"
              />
              <input
                type="number"
                placeholder="%"
                value={tier.discountPercent}
                onChange={(e) =>
                  handleTierChange(index, "discountPercent", e.target.value)
                }
                step="1"
                min="0"
                max="50"
                className={
                  validationErrors[`priceTiers.${index}.discountPercent`]
                    ? "error"
                    : ""
                }
              />
              <span className="tier-price">
                ${tier.pricePerUnit?.usd?.toFixed(2) || "0.00"}
              </span>
              <button
                type="button"
                onClick={() => removeTier(index)}
                className="remove-btn"
                disabled={formData.priceTiers.length <= 1}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addTier} className="add-btn">
            <FontAwesomeIcon icon={faPlus} /> Add Tier
          </button>
        </div>
      )}

      <div className="tiers-toggle">
        <button
          type="button"
          onClick={() => setShowBulkOptions(!showBulkOptions)}
          className="toggle-btn"
        >
          <FontAwesomeIcon icon={showBulkOptions ? faEye : faEyeSlash} />
          {showBulkOptions ? " Hide" : " Show"} Bulk Options
        </button>
      </div>
      {showBulkOptions && (
        <div className="bulk-options-section">
          <h4>
            <FontAwesomeIcon icon={faBox} /> Bulk Options (Quick Select)
          </h4>
          <div className="bulk-header">
            <span>Qty (kg)</span>
            <span>Label</span>
            <span>Discount %</span>
            <span>Active</span>
            <span></span>
          </div>
          {formData.bulkOptions.map((option, index) => (
            <div key={index} className="bulk-row">
              <input
                type="number"
                placeholder="Qty"
                value={option.quantity}
                onChange={(e) =>
                  handleBulkOptionChange(index, "quantity", e.target.value)
                }
                min="0"
                className={
                  validationErrors[`bulkOptions.${index}.quantity`]
                    ? "error"
                    : ""
                }
              />
              <input
                type="text"
                placeholder="Label"
                value={option.label}
                onChange={(e) =>
                  handleBulkOptionChange(index, "label", e.target.value)
                }
                className={
                  validationErrors[`bulkOptions.${index}.label`] ? "error" : ""
                }
              />
              <input
                type="number"
                placeholder="%"
                value={option.discountPercent}
                onChange={(e) =>
                  handleBulkOptionChange(
                    index,
                    "discountPercent",
                    e.target.value,
                  )
                }
                step="1"
                min="0"
                max="50"
                className={
                  validationErrors[`bulkOptions.${index}.discountPercent`]
                    ? "error"
                    : ""
                }
              />
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={option.isActive}
                  onChange={(e) =>
                    handleBulkOptionChange(index, "isActive", e.target.checked)
                  }
                />
              </label>
              <button
                type="button"
                onClick={() => removeBulkOption(index)}
                className="remove-btn"
                disabled={formData.bulkOptions.length <= 1}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
            </div>
          ))}
          <button type="button" onClick={addBulkOption} className="add-btn">
            <FontAwesomeIcon icon={faPlus} /> Add Bulk Option
          </button>
        </div>
      )}
    </div>
  );

  const renderDescriptionsTab = () => (
    <div className="product-form-section">
      <h3>
        <FontAwesomeIcon icon={faClipboard} /> Descriptions
      </h3>

      <div className="form-group">
        <label>Full Description *</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows="5"
          className={validationErrors.description ? "error" : ""}
          placeholder="Detailed product description with features and benefits..."
        />
        {validationErrors.description && (
          <span className="error-text">{validationErrors.description}</span>
        )}
        <small>{formData.description.length} characters</small>
      </div>

      <div className="form-group">
        <label>Usage Instructions</label>
        <textarea
          name="usageInstructions"
          value={formData.usageInstructions}
          onChange={handleChange}
          rows="3"
          placeholder="How to use this product..."
        />
      </div>

      <div className="form-group">
        <label>
          <FontAwesomeIcon icon={faStar} /> Benefits
        </label>
        <div className="tags-list">
          {formData.benefits.map((benefit, index) => (
            <div key={index} className="tag-item">
              <span>{benefit}</span>
              <button type="button" onClick={() => handleRemoveBenefit(index)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          ))}
        </div>
        <div className="add-tag-row">
          <input
            type="text"
            placeholder="Add benefit (e.g., Boosts milk production)"
            value={newBenefit}
            onChange={(e) => setNewBenefit(e.target.value)}
            onKeyPress={(e) =>
              e.key === "Enter" && (e.preventDefault(), handleAddBenefit())
            }
          />
          <button type="button" onClick={handleAddBenefit} className="add-btn">
            <FontAwesomeIcon icon={faPlus} /> Add
          </button>
        </div>
      </div>
    </div>
  );

  const renderSpecsTab = () => (
    <div className="product-form-section">
      <h3>
        <FontAwesomeIcon icon={faInfoCircle} /> Specifications
      </h3>
      <div className="specs-list">
        {Object.entries(formData.specifications).map(([key, value]) => (
          <div key={key} className="spec-item">
            <span className="spec-key">{key}:</span>
            <span className="spec-value">{value}</span>
            <button
              type="button"
              onClick={() => handleRemoveSpecification(key)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        ))}
      </div>
      <div className="add-spec-row">
        <input
          type="text"
          placeholder="Specification name"
          value={newSpecKey}
          onChange={(e) => setNewSpecKey(e.target.value)}
        />
        <input
          type="text"
          placeholder="Value"
          value={newSpecValue}
          onChange={(e) => setNewSpecValue(e.target.value)}
        />
        <button
          type="button"
          onClick={handleAddSpecification}
          className="add-btn"
        >
          <FontAwesomeIcon icon={faPlus} /> Add
        </button>
      </div>
    </div>
  );

  const renderInventoryTab = () => (
    <div className="product-form-section">
      <h3>
        <FontAwesomeIcon icon={faBox} /> Inventory
      </h3>

      <div className="form-row">
        <div className="form-group">
          <label>Stock Quantity</label>
          <input
            type="number"
            name="stock"
            value={formData.stock}
            onChange={handleChange}
            min="0"
            className={validationErrors.stock ? "error" : ""}
          />
          {validationErrors.stock && (
            <span className="error-text">{validationErrors.stock}</span>
          )}
        </div>
        <div className="form-group">
          <label>Low Stock Threshold</label>
          <input
            type="number"
            name="lowStockThreshold"
            value={formData.lowStockThreshold}
            onChange={handleChange}
            min="0"
            className={validationErrors.lowStockThreshold ? "error" : ""}
          />
          {validationErrors.lowStockThreshold && (
            <span className="error-text">
              {validationErrors.lowStockThreshold}
            </span>
          )}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Shipping Weight (kg)</label>
          <input
            type="number"
            name="shippingWeight"
            value={formData.shippingWeight}
            onChange={handleChange}
            step="0.01"
            min="0"
          />
        </div>
        <div className="form-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              name="hazmat"
              checked={formData.hazmat}
              onChange={handleChange}
            />
            <FontAwesomeIcon icon={faExclamationTriangle} /> Hazardous Material
          </label>
        </div>
      </div>
    </div>
  );

  const renderImagesTab = () => (
    <div className="product-form-section">
      <h3>
        <FontAwesomeIcon icon={faImage} /> Product Images
      </h3>

      <div className="form-group">
        <label>Upload Images</label>
        <div className="file-upload-area">
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            className="file-input"
            id="product-images"
          />
          <label htmlFor="product-images" className="file-upload-label">
            <FontAwesomeIcon icon={faUpload} />
            <span>Click or drag to upload images</span>
            <small>
              Max 5 images, 5MB each. Supported: JPG, PNG, WEBP, GIF
            </small>
          </label>
        </div>
      </div>

      {images.length > 0 && (
        <div className="image-preview-grid">
          {images.map((img, index) => (
            <div key={index} className="image-preview-item">
              <img src={URL.createObjectURL(img)} alt={`Preview ${index}`} />
              <button type="button" onClick={() => removeImage(index)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          ))}
        </div>
      )}

      {existingImages.length > 0 && (
        <div className="image-preview-grid">
          {existingImages.map((img, index) => (
            <div key={index} className="image-preview-item existing">
              <img src={img} alt={`Product ${index}`} />
              <button type="button" onClick={() => removeExistingImage(index)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderSupplierTab = () => (
    <div className="product-form-section">
      <h3>
        <FontAwesomeIcon icon={faShip} /> Supplier Information
      </h3>

      <div className="supplier-info-card">
        <FontAwesomeIcon icon={faShip} className="supplier-icon" />
        <div className="supplier-info">
          <strong>{SUPPLIER.name}</strong>
          <span>
            <FontAwesomeIcon icon={faGlobe} /> {SUPPLIER.country} &bull;{" "}
            <FontAwesomeIcon icon={faAnchor} /> {SUPPLIER.portLoading}
          </span>
        </div>
        <span className="supplier-badge">
          <FontAwesomeIcon icon={faCheckCircle} /> Quality Assured
        </span>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faBuilding} /> Supplier Name
          </label>
          <input
            type="text"
            name="supplierName"
            value={formData.supplierName}
            onChange={handleChange}
            placeholder={SUPPLIER.name}
          />
        </div>
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faGlobe} /> Supplier Country
          </label>
          <input
            type="text"
            name="supplierCountry"
            value={formData.supplierCountry}
            onChange={handleChange}
            placeholder={SUPPLIER.country}
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faLocationDot} /> Supplier City
          </label>
          <input
            type="text"
            name="supplierCity"
            value={formData.supplierCity}
            onChange={handleChange}
            placeholder={SUPPLIER.city}
          />
        </div>
        <div className="form-group">
          <label>
            <FontAwesomeIcon icon={faAnchor} /> Port of Loading
          </label>
          <input
            type="text"
            name="portLoading"
            value={formData.portLoading}
            onChange={handleChange}
            placeholder={SUPPLIER.portLoading}
          />
        </div>
      </div>

      <div className="form-group">
        <label>
          <FontAwesomeIcon icon={faShip} /> Port of Discharge
        </label>
        <input
          type="text"
          name="portDischarge"
          value={formData.portDischarge}
          onChange={handleChange}
          placeholder={SUPPLIER.portDischarge}
        />
      </div>

      <div className="supplier-tip">
        <FontAwesomeIcon icon={faInfoCircle} />
        <span>
          Products are sourced from{" "}
          <strong>{formData.supplierName || SUPPLIER.name}</strong>. All
          products are globally sourced for premium quality.
        </span>
      </div>
    </div>
  );

  const renderSEOTab = () => (
    <div className="product-form-section">
      <h3>
        <FontAwesomeIcon icon={faGlobe} /> SEO
      </h3>

      <div className="form-group">
        <label>Meta Title</label>
        <input
          type="text"
          name="metaTitle"
          value={formData.metaTitle}
          onChange={handleChange}
          placeholder="SEO title"
        />
        <small>{formData.metaTitle.length} characters</small>
      </div>

      <div className="form-group">
        <label>Meta Description</label>
        <textarea
          name="metaDescription"
          value={formData.metaDescription}
          onChange={handleChange}
          rows="2"
          placeholder="SEO description"
        />
        <small>{formData.metaDescription.length} characters</small>
      </div>

      <div className="form-group">
        <label>Meta Keywords</label>
        <input
          type="text"
          name="metaKeywords"
          value={formData.metaKeywords.join(", ")}
          onChange={(e) =>
            setFormData({
              ...formData,
              metaKeywords: e.target.value
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          placeholder="keyword1, keyword2, keyword3"
        />
        <small>Comma separated keywords</small>
      </div>
    </div>
  );

  const renderStatusTab = () => (
    <div className="product-form-section">
      <h3>
        <FontAwesomeIcon icon={faCheck} /> Status & Features
      </h3>

      <div className="form-row">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="isAvailable"
            checked={formData.isAvailable}
            onChange={handleChange}
          />
          <FontAwesomeIcon icon={formData.isAvailable ? faCheck : faTimes} />{" "}
          Available for Sale
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="isPreOrderAvailable"
            checked={formData.isPreOrderAvailable}
            onChange={handleChange}
          />
          <FontAwesomeIcon
            icon={formData.isPreOrderAvailable ? faCheck : faTimes}
          />{" "}
          Available for Pre-Order
        </label>
      </div>

      <div className="form-row">
        <label className="checkbox-label">
          <input
            type="checkbox"
            name="featured"
            checked={formData.featured}
            onChange={handleChange}
          />
          <FontAwesomeIcon icon={faStar} /> Featured Product
        </label>
        {formData.featured && (
          <div className="form-group">
            <label>Featured Order</label>
            <input
              type="number"
              name="featuredOrder"
              value={formData.featuredOrder}
              onChange={handleChange}
              min="0"
              placeholder="Lower numbers appear first"
            />
          </div>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Average Rating</label>
          <input
            type="number"
            value={formData.averageRating}
            disabled
            step="0.1"
            min="0"
            max="5"
          />
          <small>Auto-calculated from user reviews</small>
        </div>
        <div className="form-group">
          <label>Total Comments</label>
          <input type="number" value={formData.commentCount} disabled />
          <small>Auto-calculated from user comments</small>
        </div>
      </div>

      <div className="supplier-origin-info">
        <FontAwesomeIcon icon={faShip} />
        <span>
          Product sourced from{" "}
          <strong>{formData.supplierName || SUPPLIER.name}</strong> (
          {formData.supplierCountry || SUPPLIER.country})
        </span>
        <span className="exchange-rate-badge">
          <FontAwesomeIcon icon={faMoneyBillWave} /> {USD_TO_SLL_RATE} SLL/USD
        </span>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "basic":
        return renderBasicTab();
      case "pricing":
        return renderPricingTab();
      case "descriptions":
        return renderDescriptionsTab();
      case "specs":
        return renderSpecsTab();
      case "inventory":
        return renderInventoryTab();
      case "images":
        return renderImagesTab();
      case "supplier":
        return renderSupplierTab();
      case "seo":
        return renderSEOTab();
      case "status":
        return renderStatusTab();
      default:
        return renderBasicTab();
    }
  };

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content product-form-modal large"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>
            {product ? (
              <>
                <FontAwesomeIcon icon={faEdit} /> Edit Product
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faPlus} /> Add New Product
              </>
            )}
            <span className="supplier-tag">
              <FontAwesomeIcon icon={faShip} /> {SUPPLIER.name}
            </span>
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {error && (
          <div className="alert-message error">
            <FontAwesomeIcon icon={faExclamationTriangle} /> {error}
          </div>
        )}
        {success && (
          <div className="alert-message success">
            <FontAwesomeIcon icon={faCheck} /> {success}
          </div>
        )}

        {renderTabs()}

        <form onSubmit={handleSubmit} className="product-form">
          {renderContent()}

          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                </>
              ) : product ? (
                <>
                  <FontAwesomeIcon icon={faSave} /> Update Product
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlus} /> Create Product
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductForm;
